import type { ValidationErrors } from '../../../../../utils/validation';
import type { IRedenen } from './RedenenAddEditModal';
import { CheckMarkButton } from '../../../../elements/buttons/checkMarkButton/CheckMarkButton';
import { FormInlineGroup, FormParagraph, NullableInputText } from '../../../../elements/common';
import { Component } from '../../../../elements/Component';
import { FormLabel } from '../../../../elements/forms/FormLabel';
import { HorizontalFormGroup } from '../../../../elements/forms/HorizontalFormGroup';
import { HorizontalFormGroupRead } from '../../../../elements/forms/horizontalFormGroupRead/HorizontalFormGroupRead';
import { InputContainer } from '../../../../elements/forms/InputContainer';
import { RedenenAddEditModal } from './RedenenAddEditModal';

interface IRedenenCustomControlData {
    reden: string | null;
    redenen: IRedenen | null;
}

interface IRedenenAddEditProps {
    customControlData: IRedenenCustomControlData;
    errors: ValidationErrors;
    handleChange: (customControl: Partial<IRedenenCustomControlData>) => Promise<void>;
    isRequired: boolean;
    validateOne: (name: keyof IRedenenCustomControlData) => Promise<void>;
}

interface IRedenenAddEditState {
    showModal?: boolean;
}

export class RedenenAddEdit extends Component<IRedenenAddEditProps, IRedenenAddEditState> {
    public render() {
        return (
            <>
                <HorizontalFormGroup
                    hasErrors={
                        this.props.errors.has('customControlData.reden') ||
                        this.props.errors.has('customControlData.redenen')
                    }
                >
                    <FormLabel isRequired={this.props.isRequired}>Reden van het ECG</FormLabel>
                    <InputContainer>
                        <NullableInputText
                            error={this.props.errors.get('customControlData.reden')}
                            onChange={async (value) => await this.props.handleChange({ reden: value })}
                            onDoValidate={async () => await this.props.validateOne('reden')}
                            placeholder="Omschrijf de reden of klik en kies uit de lijst..."
                            value={this.props.customControlData.reden}
                        />
                    </InputContainer>
                    <CheckMarkButton
                        automation="cardioButtonReasons"
                        hasError={
                            this.props.errors.has('customControlData.reden') ||
                            this.props.errors.has('customControlData.redenen')
                        }
                        isChecked={!!this.props.customControlData.redenen}
                        onClick={async () => await this.setState({ showModal: true })}
                    >
                        Redenen
                    </CheckMarkButton>
                </HorizontalFormGroup>

                {this.state.showModal && (
                    <RedenenAddEditModal
                        anamneseData={this.props.customControlData.redenen}
                        deleteAnamneseData={async () => {
                            await this.props.handleChange({ redenen: null });
                            await this.props.validateOne('reden');
                            await this.setState({ showModal: false });
                        }}
                        onClose={async () => await this.setState({ showModal: false })}
                        setAnamneseData={async (reden) => {
                            await this.props.handleChange({ redenen: reden });
                            await this.props.validateOne('reden');
                            await this.setState({ showModal: false });
                        }}
                    />
                )}
            </>
        );
    }
}

export function RedenenRead(props: { customControlData: IRedenenCustomControlData }) {
    return (
        <HorizontalFormGroupRead>
            <FormLabel>Reden van het ECG</FormLabel>
            <InputContainer>
                <FormParagraph>
                    {props.customControlData.reden && <FormInlineGroup value={props.customControlData.reden} />}

                    {props.customControlData.redenen && (
                        <>
                            {props.customControlData.redenen.ritme_geleidingsstoornis !== null && (
                                <FormInlineGroup
                                    label={'Ritme / Geleidingsstoornis'}
                                    value={props.customControlData.redenen.ritme_geleidingsstoornis}
                                />
                            )}
                            {props.customControlData.redenen.duizeling_collapsneigingen_wegraking !== null && (
                                <FormInlineGroup
                                    label={'Duizeling / Collapsneigingen / Wegraking'}
                                    value={props.customControlData.redenen.duizeling_collapsneigingen_wegraking}
                                />
                            )}
                            {props.customControlData.redenen.hartkloppingen_overslag !== null && (
                                <FormInlineGroup
                                    label={'Hartkloppingen / Overslag'}
                                    value={props.customControlData.redenen.hartkloppingen_overslag}
                                />
                            )}
                            {props.customControlData.redenen.verdenking_ischemie_infarct !== null && (
                                <FormInlineGroup
                                    label={'Verdenking Ischemie / Infarct'}
                                    value={props.customControlData.redenen.verdenking_ischemie_infarct}
                                />
                            )}
                            {props.customControlData.redenen.typische_angina_pectoris !== null && (
                                <FormInlineGroup
                                    label={'Typische angina pectoris'}
                                    value={props.customControlData.redenen.typische_angina_pectoris}
                                />
                            )}
                            {props.customControlData.redenen.atypische_angina_pectoris !== null && (
                                <FormInlineGroup
                                    label={'Atypische angina pectoris'}
                                    value={props.customControlData.redenen.atypische_angina_pectoris}
                                />
                            )}
                            {props.customControlData.redenen.dyspnoe_d_effort !== null && (
                                <FormInlineGroup
                                    label={"Dyspnoe d'effort"}
                                    value={props.customControlData.redenen.dyspnoe_d_effort}
                                />
                            )}
                            {props.customControlData.redenen.hartfalen !== null && (
                                <FormInlineGroup
                                    label={'Hartfalen'}
                                    value={props.customControlData.redenen.hartfalen}
                                />
                            )}
                            {props.customControlData.redenen.decompensatio_cordis !== null && (
                                <FormInlineGroup
                                    label={'Decompensatio cordis'}
                                    value={props.customControlData.redenen.decompensatio_cordis}
                                />
                            )}
                            {props.customControlData.redenen.ventrikel_hypertrofie !== null && (
                                <FormInlineGroup
                                    label={'Ventrikel hypertrofie'}
                                    value={props.customControlData.redenen.ventrikel_hypertrofie}
                                />
                            )}
                            {props.customControlData.redenen.kleplijden !== null && (
                                <FormInlineGroup
                                    label={'Kleplijden'}
                                    value={props.customControlData.redenen.kleplijden}
                                />
                            )}
                            {props.customControlData.redenen.diabetes_mellitus !== null && (
                                <FormInlineGroup
                                    label={'Diabetes mellitus'}
                                    value={props.customControlData.redenen.diabetes_mellitus}
                                />
                            )}
                            {props.customControlData.redenen.hypercholesterolemie !== null && (
                                <FormInlineGroup
                                    label={'Hypercholesterolemie'}
                                    value={props.customControlData.redenen.hypercholesterolemie}
                                />
                            )}
                            {props.customControlData.redenen.familiaal_belast !== null && (
                                <FormInlineGroup
                                    label={'Familiaal belast'}
                                    value={props.customControlData.redenen.familiaal_belast}
                                />
                            )}
                            {props.customControlData.redenen.hypertensie !== null && (
                                <FormInlineGroup
                                    label={'Hypertensie'}
                                    value={props.customControlData.redenen.hypertensie}
                                />
                            )}
                            {props.customControlData.redenen.algemeen_onderzoek !== null && (
                                <FormInlineGroup
                                    label={'Algemeen onderzoek'}
                                    value={props.customControlData.redenen.algemeen_onderzoek}
                                />
                            )}
                            {props.customControlData.redenen.geruststelling !== null && (
                                <FormInlineGroup
                                    label={'Geruststelling'}
                                    value={props.customControlData.redenen.geruststelling}
                                />
                            )}
                            {props.customControlData.redenen.controle_therapie !== null && (
                                <FormInlineGroup
                                    label={'Controle therapie'}
                                    value={props.customControlData.redenen.controle_therapie}
                                />
                            )}
                            {props.customControlData.redenen.overig && (
                                <FormInlineGroup
                                    label={'Overig'}
                                    value={props.customControlData.redenen.overig}
                                />
                            )}
                        </>
                    )}
                </FormParagraph>
            </InputContainer>
        </HorizontalFormGroupRead>
    );
}
